import { useEffect, useRef, useState } from 'react';

/**
 * Reusable share affordance (Phase 1a-Sharing-UI). Given a URL + title it
 * offers: copy-to-clipboard, the OS native share sheet (navigator.share, when
 * present — mobile), and direct targets for email / WhatsApp / X / Facebook.
 *
 * Used in two places: the gallery header's "Share" button for PUBLIC galleries
 * (any viewer can share the gallery URL) and the owner's invite-link creation
 * (share the freshly-minted link, not just copy it).
 */
export function ShareModal({
  url,
  title,
  message,
  onClose,
}: {
  url: string;
  title: string;
  /** Short human label for the thing being shared, e.g. the gallery name. */
  message: string;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  const closeRef = useRef<HTMLButtonElement>(null);
  const hasNativeShare = typeof navigator !== 'undefined' && typeof navigator.share === 'function';

  useEffect(() => {
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const shareText = `${message} — ${url}`;
  const targets: { label: string; href: string }[] = [
    {
      label: 'Email',
      href: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(shareText)}`,
    },
    { label: 'WhatsApp', href: `https://wa.me/?text=${encodeURIComponent(shareText)}` },
    {
      label: 'X',
      href: `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(message)}`,
    },
    {
      label: 'Facebook',
      href: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`,
    },
  ];

  function copy() {
    navigator.clipboard.writeText(url).then(
      () => setCopied(true),
      () => setCopied(false),
    );
  }

  async function nativeShare() {
    try {
      await navigator.share({ title, text: message, url });
    } catch {
      // User dismissed the sheet, or share failed — no-op; copy stays available.
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Share gallery"
        className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between">
          <h2 className="text-base font-semibold text-neutral-900">Share “{message}”</h2>
          <button
            ref={closeRef}
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="text-neutral-400 hover:text-neutral-700"
          >
            ✕
          </button>
        </div>

        <div className="mt-4 flex items-center gap-2 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2">
          <code className="flex-1 break-all text-xs">{url}</code>
          <button
            type="button"
            onClick={copy}
            className="shrink-0 rounded-md bg-neutral-900 px-3 py-1 text-xs font-medium text-white hover:bg-neutral-700"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {hasNativeShare && (
            <button
              type="button"
              onClick={nativeShare}
              className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:border-neutral-500"
            >
              Share…
            </button>
          )}
          {targets.map((t) => (
            <a
              key={t.label}
              href={t.href}
              target="_blank"
              rel="noreferrer noopener"
              className="rounded-md border border-neutral-300 px-3 py-1.5 text-sm text-neutral-700 hover:border-neutral-500"
            >
              {t.label}
            </a>
          ))}
        </div>
      </div>
    </div>
  );
}
